/**
 * Synchronizes custom Vogue wishlist controls with Smart Wishlist.
 *
 * Smart Wishlist exposes a public API and JavaScript helpers on the storefront.
 * This controller keeps the theme's custom heart buttons and wishlist page in
 * sync with that app without relying on the app's default markup.
 */
'use strict';

{
  const SMART_WISHLIST_API_PATH = '/a/wishlist';
  const SMART_WISHLIST_BUTTON_SELECTOR = '[data-vo-smartwishlist-button]';
  const SMART_WISHLIST_HEADER_SELECTOR = '[data-vo-smartwishlist-header]';
  const SMART_WISHLIST_PAGE_SELECTOR = '[data-vo-wishlist-page]';
  const WISHLIST_COUNT_SELECTOR = '[data-vo-wishlist-count]';
  const WISHLIST_CACHE_KEY = 'wishlist';
  const WISHLIST_SESSION_META_KEY = `${WISHLIST_CACHE_KEY}_meta`;
  const WISHLIST_PENDING_MERGE_KEY = `${WISHLIST_CACHE_KEY}_pending`;
  const WISHLIST_ACTION_BUTTON_CLASS = 'tw-vo-btn-filled-sm tw-w-full tw-text-center';
  const WISHLIST_ACTION_LINK_CLASS = `${WISHLIST_ACTION_BUTTON_CLASS} tw-no-underline`;
  const WISHLIST_ADD_TO_CART_BUTTON_CLASS = `${WISHLIST_ACTION_BUTTON_CLASS} disabled:tw-cursor-default disabled:tw-bg-[#22222380]`;
  const REQUEST_TIMEOUT_MS = 8000;
  const WISHLIST_API_MIN_PAGE_SIZE = 5;
  const WISHLIST_API_MAX_PAGES = 100;
  const WISHLIST_PLP_CARD_SECTION_ID = 'vo-wishlist-plp-card';
  const PENDING_WISHLIST_MUTATION_TTL_MS = 5000;
  const SMART_WISHLIST_CALLBACKS = [
    'SWCallbackExpressWishlistProductItemAdded',
    'SWCallbackExpressWishlistProductItemRemoved',
    'SWCallbackExpressWishlistCollectionItemAdded',
    'SWCallbackExpressWishlistCollectionItemRemoved',
    'SWCallbackCustomerWishlistProductItemAdded',
    'SWCallbackCustomerWishlistProductItemRemoved',
    'SWCallbackCustomerWishlistCollectionItemAdded',
    'SWCallbackCustomerWishlistCollectionItemRemoved',
  ];
  const HEART_ICON = `
    <svg xmlns="http://www.w3.org/2000/svg" class="tw-block tw-w-full tw-h-full" viewBox="0 0 20 20" aria-hidden="true">
      <path data-vo-heart-fill fill="currentColor" d="M10 17.495 2.305 9.901a4.293 4.293 0 0 1 0-6.128 4.433 4.433 0 0 1 6.21 0L10 5.24l1.485-1.467a4.433 4.433 0 0 1 6.21 0 4.293 4.293 0 0 1 0 6.128L10 17.495Z"></path>
      <path data-vo-heart-outline fill="currentColor" fill-rule="evenodd" d="M10 5.24 8.515 3.773a4.433 4.433 0 0 0-6.21 0 4.293 4.293 0 0 0 0 6.128L10 17.495l7.695-7.593a4.293 4.293 0 0 0 0-6.128 4.433 4.433 0 0 0-6.21 0zm.765-2.177c2.113-2.084 5.538-2.084 7.65 0a5.29 5.29 0 0 1 0 7.55l-7.695 7.593a1.03 1.03 0 0 1-1.44 0l-7.696-7.594a5.29 5.29 0 0 1 0-7.549C3.697.98 7.122.98 9.234 3.063l.766.755z"></path>
    </svg>
  `.trim();
  const EMPTY_SNAPSHOT = { count: 0, items: [] };
  const sharedState = {
    snapshot: EMPTY_SNAPSHOT,
    snapshotPromise: null,
    refreshTimeout: null,
    buttonObserver: null,
    pendingMutationRefreshTimeout: null,
    pendingWishlistMutations: new Map(),
    hasLoadedSnapshot: false,
    initializedButtons: new WeakSet(),
    pageControllers: new Map(),
    isInitialized: false,
    pageShowHandler: null,
    sessionSyncPromise: null,
    preserveCachedSnapshotOnNextEmptyRefresh: false,
  };
  const productInfoCache = new Map();
  const wishlistPlpCardMarkupCache = new Map();

  /**
   * Escapes text before it is interpolated into HTML strings.
   *
   * @param {string} value
   * @returns {string}
   */
  function escapeHtml(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  /**
   * Reads a cookie value from the current document.
   *
   * @param {string} name
   * @returns {string}
   */
  function readCookie(name) {
    const target = `${name}=`;
    const cookies = decodeURIComponent(document.cookie || '').split(';');

    for (const entry of cookies) {
      const cookie = entry.trim();
      if (cookie.startsWith(target)) {
        return cookie.slice(target.length);
      }
    }

    return '';
  }

  /**
   * Reads the optimistic wishlist cache used by the Smart Wishlist integration.
   *
   * @returns {Array<object>}
   */
  function readCachedWishlist() {
    return readCachedWishlistRecord().items;
  }

  /**
   * Reads the optimistic wishlist cache and reports whether a stored snapshot exists.
   *
   * @returns {{hasCache: boolean, items: Array<object>}}
   */
  function readCachedWishlistRecord() {
    try {
      const cached = window.localStorage?.getItem(WISHLIST_CACHE_KEY);
      if (cached === null || cached === undefined) {
        return {
          hasCache: false,
          items: [],
        };
      }

      const parsed = cached ? JSON.parse(cached) : [];
      return {
        hasCache: true,
        items: Array.isArray(parsed) ? parsed.map(normalizeWishlistItem).filter(Boolean) : [],
      };
    } catch (error) {
      console.warn('Smart Wishlist cache could not be read.', error);
      return {
        hasCache: false,
        items: [],
      };
    }
  }

  /**
   * Stores the latest normalized wishlist snapshot for fast UI recovery.
   *
   * @param {Array<object>} items
   * @returns {void}
   */
  function writeCachedWishlist(items) {
    try {
      window.localStorage?.setItem(WISHLIST_CACHE_KEY, JSON.stringify(items));
    } catch (error) {
      console.warn('Smart Wishlist cache could not be written.', error);
    }
  }

  /**
   * Removes the optimistic wishlist cache.
   *
   * @returns {void}
   */
  function clearCachedWishlist() {
    try {
      window.localStorage?.removeItem(WISHLIST_CACHE_KEY);
    } catch (error) {
      console.warn('Smart Wishlist cache could not be cleared.', error);
    }
  }

  /**
   * Reads a JSON object from localStorage.
   *
   * @param {string} key
   * @returns {unknown|null}
   */
  function readStoredJson(key) {
    try {
      const value = window.localStorage?.getItem(key);
      return value ? JSON.parse(value) : null;
    } catch (error) {
      console.warn(`Smart Wishlist storage key "${key}" could not be read.`, error);
      return null;
    }
  }

  /**
   * Writes or removes a JSON object in localStorage.
   *
   * @param {string} key
   * @param {unknown|null} value
   * @returns {void}
   */
  function writeStoredJson(key, value) {
    try {
      if (value === null || value === undefined) {
        window.localStorage?.removeItem(key);
        return;
      }

      window.localStorage?.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.warn(`Smart Wishlist storage key "${key}" could not be written.`, error);
    }
  }

  /**
   * Reads the local wishlist cache ownership metadata.
   *
   * @returns {{ownerType?: string, customerId?: string|null, lastCustomerId?: string|null}|null}
   */
  function getWishlistSessionMeta() {
    const meta = readStoredJson(WISHLIST_SESSION_META_KEY);
    return meta && typeof meta === 'object' ? meta : null;
  }

  /**
   * Stores the local wishlist cache ownership metadata.
   *
   * @param {{ownerType: string, customerId?: string|null, lastCustomerId?: string|null}|null} meta
   * @returns {void}
   */
  function setWishlistSessionMeta(meta) {
    writeStoredJson(WISHLIST_SESSION_META_KEY, meta);
  }

  /**
   * Reads guest items waiting to be merged into a customer wishlist.
   *
   * @returns {{customerId?: string, items?: Array<object>}|null}
   */
  function getPendingWishlistMerge() {
    const pending = readStoredJson(WISHLIST_PENDING_MERGE_KEY);
    return pending && typeof pending === 'object' ? pending : null;
  }

  /**
   * Stores or clears guest items waiting to be merged into a customer wishlist.
   *
   * @param {{customerId: string, items: Array<object>, createdAt: string}|null} pending
   * @returns {void}
   */
  function setPendingWishlistMerge(pending) {
    writeStoredJson(WISHLIST_PENDING_MERGE_KEY, pending);
  }

  /**
   * Returns the active customer id as a stable string.
   *
   * @returns {string}
   */
  function getCurrentCustomerKey() {
    const customerId = getCustomerWishlistId();
    return customerId > 0 ? String(customerId) : '';
  }

  /**
   * Expires Smart Wishlist's anonymous wishlist cookie.
   *
   * @returns {void}
   */
  function clearWishlistCookie() {
    try {
      const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
      document.cookie = `wishlist_id=; ${expiry}; path=/`;

      if (window.location?.hostname) {
        document.cookie = `wishlist_id=; ${expiry}; path=/; domain=${window.location.hostname}`;
      }
    } catch (error) {
      console.warn('Smart Wishlist guest cookie could not be cleared.', error);
    }
  }

  /**
   * Clears the guest wishlist state used by anonymous Smart Wishlist sessions.
   *
   * @returns {void}
   */
  function clearExpressWishlistState() {
    clearCachedWishlist();
    clearWishlistCookie();
    sharedState.snapshot = EMPTY_SNAPSHOT;
    sharedState.snapshotPromise = null;
    sharedState.hasLoadedSnapshot = false;
  }

  /**
   * Marks the optimistic cache as belonging to the current shopper state.
   *
   * @returns {void}
   */
  function setWishlistSessionMetaForCurrentOwner() {
    const customerId = getCurrentCustomerKey();
    const meta = getWishlistSessionMeta();

    if (customerId) {
      setWishlistSessionMeta({
        ownerType: 'customer',
        customerId,
        lastCustomerId: customerId,
      });
      return;
    }

    setWishlistSessionMeta({
      ownerType: 'guest',
      customerId: null,
      lastCustomerId: meta?.lastCustomerId || meta?.customerId || null,
    });
  }

  /**
   * Returns the current customer wishlist id when account integration is active.
   *
   * @returns {number}
   */
  function getCustomerWishlistId() {
    if (typeof window.SWGetCustomerWishlistId === 'function') {
      try {
        const customerId = Number(window.SWGetCustomerWishlistId());
        if (Number.isFinite(customerId) && customerId > 0) {
          return customerId;
        }
      } catch (error) {
        console.warn('Smart Wishlist customer id could not be resolved.', error);
      }
    }

    const analyticsCustomerId = Number(window.ShopifyAnalytics?.meta?.page?.customerId || '');
    if (Number.isFinite(analyticsCustomerId) && analyticsCustomerId > 0) {
      return analyticsCustomerId;
    }

    const liquidCustomerId = Number(document.body?.dataset.voCustomerId || '');
    return Number.isFinite(liquidCustomerId) ? liquidCustomerId : 0;
  }

  /**
   * Returns the Smart Wishlist express wishlist id for anonymous visitors.
   *
   * @returns {string}
   */
  function getExpressWishlistId() {
    if (typeof window.SWGetExpressWishlistId === 'function') {
      try {
        return String(window.SWGetExpressWishlistId() || '');
      } catch (error) {
        console.warn('Smart Wishlist express id could not be resolved.', error);
      }
    }

    return readCookie('wishlist_id');
  }

  /**
   * Builds the Smart Wishlist public API URL for the current shopper.
   *
   * @returns {string|null}
   */
  function buildWishlistApiUrl(page = 1) {
    const customerId = getCustomerWishlistId();
    const cacheBuster = Date.now();

    if (customerId > 0) {
      return `${SMART_WISHLIST_API_PATH}?type=api&customerid=${customerId}&version=1&page=${page}&_=${cacheBuster}`;
    }

    const wishlistId = getExpressWishlistId();
    if (!wishlistId) return null;

    return `${SMART_WISHLIST_API_PATH}?type=api&wishlistid=${encodeURIComponent(wishlistId)}&version=1&page=${page}&_=${cacheBuster}`;
  }

  /**
   * Normalizes the different Smart Wishlist item shapes into one local shape.
   *
   * @param {object} item
   * @returns {object|null}
   */
  function normalizeWishlistItem(item) {
    if (!item || typeof item !== 'object') return null;

    const productId = item.product_id ?? item.productId ?? item.id ?? '';
    const variantId = item.variant_id ?? item.variantId ?? item.variant?.id ?? '';
    const category = String(item.category ?? item.product_type ?? item.productType ?? '')
      .trim()
      .toLowerCase();

    return {
      ...item,
      id: productId,
      product_id: productId,
      variant_id: variantId,
      category,
    };
  }

  /**
   * Normalizes the Smart Wishlist API payload into a stable local shape.
   *
   * @param {unknown} payload
   * @returns {{count: number, items: Array<object>}}
   */
  function normalizeWishlistResponse(payload) {
    if (!payload || typeof payload !== 'object') {
      return EMPTY_SNAPSHOT;
    }

    const items = Array.isArray(payload.items) ? payload.items.map(normalizeWishlistItem).filter(Boolean) : [];
    const count = Number.parseInt(payload.count, 10);

    return {
      count: Number.isFinite(count) ? count : items.length,
      items,
    };
  }

  /**
   * Fetches JSON with a hard timeout so the page can leave loading state.
   *
   * @param {string} url
   * @returns {Promise<Response>}
   */
  async function fetchWithTimeout(url, accept = 'application/json') {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timeout = window.setTimeout(() => {
      controller?.abort();
    }, REQUEST_TIMEOUT_MS);

    try {
      return await fetch(url, {
        credentials: 'same-origin',
        headers: {
          Accept: accept,
        },
        signal: controller?.signal,
      });
    } finally {
      window.clearTimeout(timeout);
    }
  }

  /**
   * Waits briefly for Smart Wishlist's mutation helpers to become available.
   *
   * @returns {Promise<boolean>}
   */
  function waitForSmartWishlistMutationApi() {
    if (typeof window.AddToSmartWishlist === 'function' && typeof window.RemoveFromSmartWishlist === 'function') {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      const startedAt = Date.now();
      const interval = window.setInterval(() => {
        const isReady =
          typeof window.AddToSmartWishlist === 'function' && typeof window.RemoveFromSmartWishlist === 'function';

        if (isReady || Date.now() - startedAt >= REQUEST_TIMEOUT_MS) {
          window.clearInterval(interval);
          resolve(isReady);
        }
      }, 50);
    });
  }

  /**
   * Waits for Smart Wishlist async mutations to settle before refetching.
   *
   * @param {number} ms
   * @returns {Promise<void>}
   */
  function delay(ms) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }

  /**
   * Returns the normalized variant id used for merge de-duplication.
   *
   * @param {object} item
   * @returns {string}
   */
  function getWishlistItemVariantKey(item) {
    return String(normalizeWishlistItem(item)?.variant_id || '');
  }

  /**
   * Returns a stable key for paginated API de-duplication.
   *
   * @param {object} item
   * @returns {string}
   */
  function getWishlistItemIdentityKey(item) {
    const normalizedItem = normalizeWishlistItem(item);
    if (!normalizedItem) return '';

    return String(
      normalizedItem.variant_id ||
        normalizedItem.product_id ||
        normalizedItem.id ||
        normalizedItem.handle ||
        normalizedItem.title ||
        ''
    );
  }

  /**
   * Merges wishlist item lists without letting stale API subsets drop cached local additions.
   *
   * @param {Array<object>} primaryItems
   * @param {Array<object>} fallbackItems
   * @returns {Array<object>}
   */
  function mergeWishlistItemLists(primaryItems, fallbackItems) {
    const items = [];
    const seenItemKeys = new Set();
    const appendItem = (item) => {
      const normalizedItem = normalizeWishlistItem(item);
      if (!normalizedItem) return;

      const itemKey = getWishlistItemIdentityKey(normalizedItem);
      if (itemKey && seenItemKeys.has(itemKey)) return;

      if (itemKey) {
        seenItemKeys.add(itemKey);
      }

      items.push(normalizedItem);
    };

    if (Array.isArray(primaryItems)) {
      primaryItems.forEach(appendItem);
    }

    if (Array.isArray(fallbackItems)) {
      fallbackItems.forEach(appendItem);
    }

    return items;
  }

  /**
   * Keeps locally cached additions when the Smart Wishlist API returns an older non-empty snapshot.
   *
   * @param {{count: number, items: Array<object>}} snapshot
   * @param {Array<object>} cachedItems
   * @returns {{count: number, items: Array<object>}}
   */
  function mergeWishlistSnapshotWithCachedItems(snapshot, cachedItems) {
    const snapshotItems = Array.isArray(snapshot.items) ? snapshot.items : [];

    if (!snapshotItems.length || !Array.isArray(cachedItems) || !cachedItems.length) {
      return snapshot;
    }

    const mergedItems = mergeWishlistItemLists(snapshotItems, cachedItems);
    if (mergedItems.length === snapshotItems.length) return snapshot;

    return {
      count: mergedItems.length,
      items: mergedItems,
    };
  }

  /**
   * Clears pending optimistic mutations after their reconciliation window.
   *
   * @returns {void}
   */
  function purgeExpiredPendingWishlistMutations() {
    const now = Date.now();

    sharedState.pendingWishlistMutations.forEach((pendingMutation, variantId) => {
      if (pendingMutation.expiresAt <= now) {
        sharedState.pendingWishlistMutations.delete(variantId);
      }
    });
  }

  /**
   * Returns the next delay needed to reconcile pending optimistic mutations.
   *
   * @returns {number}
   */
  function getPendingWishlistMutationRefreshDelay() {
    let nextExpiresAt = Infinity;

    sharedState.pendingWishlistMutations.forEach((pendingMutation) => {
      nextExpiresAt = Math.min(nextExpiresAt, pendingMutation.expiresAt);
    });

    if (!Number.isFinite(nextExpiresAt)) {
      return PENDING_WISHLIST_MUTATION_TTL_MS + 250;
    }

    return Math.max(250, nextExpiresAt - Date.now() + 250);
  }

  /**
   * Schedules a final API reconciliation after optimistic mutations expire.
   *
   * @returns {void}
   */
  function schedulePendingWishlistMutationRefresh() {
    if (sharedState.pendingMutationRefreshTimeout) return;

    sharedState.pendingMutationRefreshTimeout = window.setTimeout(() => {
      sharedState.pendingMutationRefreshTimeout = null;
      refreshWishlistUI({ force: true, showBusy: false }).finally(() => {
        if (sharedState.pendingWishlistMutations.size) {
          schedulePendingWishlistMutationRefresh();
        }
      });
    }, getPendingWishlistMutationRefreshDelay());
  }

  /**
   * Tracks a local mutation so stale API snapshots cannot immediately undo it.
   *
   * @param {'add'|'remove'} action
   * @param {object} item
   * @returns {void}
   */
  function recordPendingWishlistMutation(action, item) {
    if (action !== 'add' && action !== 'remove') return;

    const wishlistItem = normalizeWishlistItem(item);
    const variantId = getWishlistItemVariantKey(wishlistItem);
    if (!variantId) return;

    sharedState.pendingWishlistMutations.set(variantId, {
      action,
      item: wishlistItem,
      expiresAt: Date.now() + PENDING_WISHLIST_MUTATION_TTL_MS,
    });
    schedulePendingWishlistMutationRefresh();
  }

  /**
   * Reconciles API snapshots with optimistic mutations still settling in the app.
   *
   * @param {{count: number, items: Array<object>}} snapshot
   * @returns {{count: number, items: Array<object>}}
   */
  function applyPendingWishlistMutations(snapshot) {
    purgeExpiredPendingWishlistMutations();

    let items = Array.isArray(snapshot.items) ? snapshot.items.map(normalizeWishlistItem).filter(Boolean) : [];

    sharedState.pendingWishlistMutations.forEach((pendingMutation, variantId) => {
      const hasItem = items.some((item) => getWishlistItemVariantKey(item) === variantId);

      if (pendingMutation.action === 'add') {
        if (hasItem) {
          sharedState.pendingWishlistMutations.delete(variantId);
          return;
        }

        items = [...items, pendingMutation.item];
        return;
      }

      if (!hasItem) {
        sharedState.pendingWishlistMutations.delete(variantId);
        return;
      }

      items = items.filter((item) => getWishlistItemVariantKey(item) !== variantId);
    });

    return {
      count: items.length,
      items,
    };
  }

  /**
   * Checks whether the local cache belongs to the current shopper context.
   *
   * @returns {boolean}
   */
  function canHydrateWishlistSnapshotFromCache() {
    const customerId = getCurrentCustomerKey();
    const meta = getWishlistSessionMeta();
    const pending = getPendingWishlistMerge();

    if (customerId) {
      if (meta?.ownerType === 'customer' && meta.customerId && String(meta.customerId) !== customerId) {
        return false;
      }

      if (meta?.ownerType === 'customer' && meta.lastCustomerId && String(meta.lastCustomerId) !== customerId) {
        return false;
      }

      return true;
    }

    return meta?.ownerType !== 'customer' && !pending;
  }

  /**
   * Uses the local snapshot to paint wishlist controls before the app/API returns.
   *
   * @returns {boolean}
   */
  function hydrateWishlistSnapshotFromCache() {
    const cached = readCachedWishlistRecord();
    if (!cached.hasCache || !canHydrateWishlistSnapshotFromCache()) return false;

    sharedState.snapshot = applyPendingWishlistMutations({
      count: cached.items.length,
      items: cached.items,
    });
    sharedState.hasLoadedSnapshot = true;
    sharedState.preserveCachedSnapshotOnNextEmptyRefresh = cached.items.length > 0;
    syncWishlistCount(sharedState.snapshot.count);
    syncHeaderIcons(sharedState.snapshot);

    return true;
  }

  /**
   * Adds one item to Smart Wishlist through the app mutation helper.
   *
   * @param {object} item
   * @returns {Promise<boolean>}
   */
  async function addSmartWishlistItem(item) {
    const wishlistItem = normalizeWishlistItem(item);
    const productId = Number(wishlistItem?.product_id || '');
    const variantId = Number(wishlistItem?.variant_id || '');

    if (!Number.isFinite(productId) || !Number.isFinite(variantId)) return false;

    const isReady = await waitForSmartWishlistMutationApi();
    if (!isReady || typeof window.AddToSmartWishlist !== 'function') {
      throw new Error('Smart Wishlist JavaScript API is unavailable.');
    }

    await window.AddToSmartWishlist(productId, variantId);
    return true;
  }

  /**
   * Merges locally cached guest items into the active customer wishlist.
   *
   * @param {string} customerId
   * @param {Array<object>} guestItems
   * @returns {Promise<{count: number, items: Array<object>}>}
   */
  async function mergeGuestWishlistIntoCustomerWishlist(customerId, guestItems) {
    const normalizedGuestItems = Array.isArray(guestItems) ? guestItems.map(normalizeWishlistItem).filter(Boolean) : [];

    if (!customerId || !normalizedGuestItems.length) {
      setPendingWishlistMerge(null);
      return fetchWishlistSnapshot({ force: true });
    }

    const customerSnapshot = await fetchWishlistSnapshot({ force: true });
    const existingVariantIds = new Set(customerSnapshot.items.map(getWishlistItemVariantKey).filter(Boolean));
    let hasMutatedCustomerWishlist = false;

    for (const item of normalizedGuestItems) {
      const variantId = getWishlistItemVariantKey(item);
      if (!variantId || existingVariantIds.has(variantId)) continue;

      const wasAdded = await addSmartWishlistItem(item);
      if (wasAdded) {
        existingVariantIds.add(variantId);
        hasMutatedCustomerWishlist = true;
      }
    }

    if (hasMutatedCustomerWishlist) {
      await delay(350);
    }

    const updatedSnapshot = await fetchWishlistSnapshot({ force: true });
    setPendingWishlistMerge(null);
    clearWishlistCookie();
    setWishlistSessionMeta({
      ownerType: 'customer',
      customerId,
      lastCustomerId: customerId,
    });

    return updatedSnapshot;
  }

  /**
   * Synchronizes local guest/customer wishlist state across login and logout.
   *
   * @returns {Promise<void>}
   */
  function syncWishlistSessionState() {
    if (sharedState.sessionSyncPromise) return sharedState.sessionSyncPromise;

    sharedState.sessionSyncPromise = (async () => {
      const customerId = getCurrentCustomerKey();
      const meta = getWishlistSessionMeta();
      const pending = getPendingWishlistMerge();
      const cachedItems = readCachedWishlist();

      if (!customerId) {
        if (meta?.ownerType === 'customer' || pending) {
          clearExpressWishlistState();
          setPendingWishlistMerge(null);
        }

        if (!meta || meta.ownerType !== 'guest') {
          setWishlistSessionMeta({
            ownerType: 'guest',
            customerId: null,
            lastCustomerId: meta?.customerId || meta?.lastCustomerId || null,
          });
        }

        return;
      }

      if (meta?.ownerType === 'customer' && meta?.lastCustomerId && String(meta.lastCustomerId) !== customerId) {
        clearCachedWishlist();
        setPendingWishlistMerge(null);
      }

      if (meta?.ownerType === 'customer' && meta.customerId && String(meta.customerId) !== customerId) {
        clearCachedWishlist();
      }

      let itemsToMerge = [];

      if (pending?.customerId === customerId && Array.isArray(pending.items)) {
        itemsToMerge = pending.items;
      } else if (cachedItems.length > 0 && meta?.ownerType !== 'customer') {
        itemsToMerge = cachedItems;
        setPendingWishlistMerge({
          customerId,
          items: itemsToMerge,
          createdAt: new Date().toISOString(),
        });
      } else if (pending && pending.customerId !== customerId) {
        setPendingWishlistMerge(null);
      }

      setWishlistSessionMeta({
        ownerType: 'customer',
        customerId,
        lastCustomerId: customerId,
      });

      if (itemsToMerge.length) {
        await mergeGuestWishlistIntoCustomerWishlist(customerId, itemsToMerge);
      }
    })()
      .catch((error) => {
        console.warn('Smart Wishlist session state could not be synchronized.', error);
      })
      .finally(() => {
        sharedState.sessionSyncPromise = null;
      });

    return sharedState.sessionSyncPromise;
  }

  /**
   * Fetches the current wishlist snapshot, caching the result between updates.
   *
   * @param {{ force?: boolean }} options
   * @returns {Promise<{count: number, items: Array<object>}>}
   */
  async function fetchWishlistSnapshot({ force = false } = {}) {
    if (!force && sharedState.snapshotPromise) {
      return sharedState.snapshotPromise;
    }

    if (!force && sharedState.snapshot !== EMPTY_SNAPSHOT) {
      return sharedState.snapshot;
    }

    const apiUrl = buildWishlistApiUrl(1);
    if (!apiUrl) {
      const cachedItems = readCachedWishlist();
      sharedState.snapshot = applyPendingWishlistMutations({
        count: cachedItems.length,
        items: cachedItems,
      });
      return sharedState.snapshot;
    }

    sharedState.snapshotPromise = (async () => {
      const items = [];
      const seenItemKeys = new Set();
      let page = 1;
      let hasMorePages = true;

      while (hasMorePages) {
        const pageUrl = buildWishlistApiUrl(page);
        if (!pageUrl) break;

        const response = await fetchWithTimeout(pageUrl);

        if (!response.ok) {
          throw new Error(`Wishlist request failed with status ${response.status}`);
        }

        const json = await response.json();
        const snapshot = normalizeWishlistResponse(json);
        const pageItems = [];

        snapshot.items.forEach((item) => {
          const itemKey = getWishlistItemIdentityKey(item);
          if (itemKey && seenItemKeys.has(itemKey)) return;

          if (itemKey) {
            seenItemKeys.add(itemKey);
          }

          pageItems.push(item);
        });

        items.push(...pageItems);

        hasMorePages =
          page < WISHLIST_API_MAX_PAGES &&
          pageItems.length > 0 &&
          snapshot.items.length >= WISHLIST_API_MIN_PAGE_SIZE;
        page += 1;
      }

      const nextSnapshot = mergeWishlistSnapshotWithCachedItems(
        applyPendingWishlistMutations({
          count: items.length,
          items,
        }),
        readCachedWishlist()
      );

      if (
        sharedState.preserveCachedSnapshotOnNextEmptyRefresh &&
        nextSnapshot.items.length === 0 &&
        sharedState.snapshot.items.length > 0
      ) {
        sharedState.preserveCachedSnapshotOnNextEmptyRefresh = false;
        return sharedState.snapshot;
      }

      sharedState.preserveCachedSnapshotOnNextEmptyRefresh = false;
      sharedState.snapshot = nextSnapshot;
      writeCachedWishlist(sharedState.snapshot.items);
      setWishlistSessionMetaForCurrentOwner();

      return sharedState.snapshot;
    })()
      .catch((error) => {
        console.warn('Smart Wishlist snapshot could not be loaded.', error);
        const cachedItems = readCachedWishlist();
        sharedState.snapshot = applyPendingWishlistMutations({
          count: cachedItems.length,
          items: cachedItems,
        });
        return sharedState.snapshot;
      })
      .finally(() => {
        sharedState.snapshotPromise = null;
      });

    return sharedState.snapshotPromise;
  }

  /**
   * Returns the product and variant ids stored on a custom wishlist button.
   *
   * @param {HTMLElement} button
   * @returns {{productId: string, variantId: string}}
   */
  function getButtonIds(button) {
    return {
      productId: String(button.dataset.productId || ''),
      variantId: resolveButtonVariantId(button),
    };
  }

  /**
   * Finds the product-info root that owns a PDP wishlist button.
   *
   * @param {HTMLElement} button
   * @returns {HTMLElement|null}
   */
  function getButtonProductInfo(button) {
    const closestProductInfo = button.closest('product-info');
    if (closestProductInfo instanceof HTMLElement) return closestProductInfo;

    const sectionId = String(button.dataset.sectionId || '');
    if (!sectionId) return null;

    return (
      Array.from(document.querySelectorAll('product-info[data-section]')).find(
        (element) => element instanceof HTMLElement && element.dataset.section === sectionId
      ) || null
    );
  }

  /**
   * Reads the selected PDP variant from the live product form.
   *
   * @param {HTMLElement} button
   * @returns {string}
   */
  function getButtonFormVariantId(button) {
    const productInfo = getButtonProductInfo(button);
    const sectionId = String(button.dataset.sectionId || productInfo?.dataset.section || '');
    const formInput =
      (sectionId && document.getElementById(`product-form-${sectionId}`)?.querySelector('input[name="id"]')) ||
      (sectionId &&
        document.getElementById(`product-form-installment-${sectionId}`)?.querySelector('input[name="id"]')) ||
      productInfo?.querySelector('form[action*="/cart/add"] input[name="id"]');

    return String(formInput?.value || '');
  }

  /**
   * Reads the selected PDP variant from the rendered variant JSON fallback.
   *
   * @param {HTMLElement} button
   * @returns {string}
   */
  function getButtonJsonVariantId(button) {
    const selectedVariantSource = getButtonProductInfo(button)?.querySelector('variant-selects [data-selected-variant]');
    if (!selectedVariantSource?.textContent) return '';

    try {
      const selectedVariant = JSON.parse(selectedVariantSource.textContent);
      return String(selectedVariant?.id || '');
    } catch (error) {
      console.warn('Smart Wishlist selected variant JSON could not be parsed.', error);
      return '';
    }
  }

  /**
   * Returns the current variant id for PDP buttons, falling back to the stored dataset.
   *
   * @param {HTMLElement} button
   * @returns {string}
   */
  function resolveButtonVariantId(button) {
    if (!button.hasAttribute('data-vo-smartwishlist-variant-sync')) {
      return String(button.dataset.variantId || '');
    }

    const variantId = getButtonFormVariantId(button) || getButtonJsonVariantId(button) || String(button.dataset.variantId || '');
    if (variantId) {
      button.dataset.variantId = variantId;
    }

    return variantId;
  }

  /**
   * Builds the accessible label for a wishlist control based on its state.
   *
   * @param {HTMLElement} button
   * @param {boolean} isActive
   * @returns {string}
   */
  function buildButtonLabel(button, isActive) {
    const title = (button.dataset.productTitle || '').trim();
    const action = isActive ? 'Remove' : 'Add';

    if (!title) {
      return `${action} to wishlist`;
    }

    return `${action} ${title} ${isActive ? 'from' : 'to'} wishlist`;
  }

  /**
   * Applies the current active/inactive UI state to a custom wishlist button.
   *
   * @param {HTMLElement} button
   * @param {boolean} isActive
   * @returns {void}
   */
  function setButtonState(button, isActive) {
    button.dataset.voState = isActive ? 'active' : 'inactive';
    button.classList.toggle('is-active', isActive);
    button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    button.setAttribute('aria-label', buildButtonLabel(button, isActive));
  }

  /**
   * Updates every rendered wishlist count label with the latest value.
   *
   * @param {number} count
   * @returns {void}
   */
  function syncWishlistCount(count) {
    const normalizedCount = Number.isFinite(count) ? count : 0;
    const label = normalizedCount === 1 ? '1 wishlist item' : `${normalizedCount} wishlist items`;

    document.querySelectorAll(WISHLIST_COUNT_SELECTOR).forEach((element) => {
      element.textContent = `(${normalizedCount})`;
      element.setAttribute('aria-label', label);
    });
  }

  /**
   * Returns the wishlist item that should drive a custom button's state.
   *
   * PDP buttons update their current variant as shoppers switch sizes/colors, but
   * the visible heart should stay stable once any variant of that product is saved.
   * When possible, prefer the current variant so remove actions still target it.
   *
   * @param {HTMLElement} button
   * @param {{count: number, items: Array<object>}} snapshot
   * @returns {object|null}
   */
  function findSnapshotItemForButton(button, snapshot) {
    const { productId, variantId } = getButtonIds(button);
    if (!productId) return null;

    const matchingItems = snapshot.items
      .map(normalizeWishlistItem)
      .filter((item) => String(item?.product_id || '') === productId);

    if (!matchingItems.length) return null;

    return matchingItems.find((item) => String(item?.variant_id || '') === variantId) || matchingItems[0];
  }

  /**
   * Checks whether a custom wishlist button matches an item in the snapshot.
   *
   * @param {HTMLElement} button
   * @param {{count: number, items: Array<object>}} snapshot
   * @returns {boolean}
   */
  function buttonMatchesSnapshot(button, snapshot) {
    return Boolean(findSnapshotItemForButton(button, snapshot));
  }

  /**
   * Synchronizes a single custom wishlist button against the latest snapshot.
   *
   * @param {HTMLElement} button
   * @param {{count: number, items: Array<object>}} snapshot
   * @returns {void}
   */
  function syncButton(button, snapshot) {
    const matchedItem = findSnapshotItemForButton(button, snapshot);

    if (matchedItem?.variant_id) {
      button.dataset.voSmartwishlistSavedVariantId = String(matchedItem.variant_id);
    } else {
      button.removeAttribute('data-vo-smartwishlist-saved-variant-id');
    }

    setButtonState(button, Boolean(matchedItem));
    button.removeAttribute('data-vo-smartwishlist-hide-until-ready');
    button.removeAttribute('aria-busy');
  }

  /**
   * Toggles the busy state on product wishlist controls during a full snapshot refresh.
   *
   * @param {boolean} isBusy
   * @returns {void}
   */
  function setWishlistControlsBusy(isBusy) {
    document.querySelectorAll(SMART_WISHLIST_BUTTON_SELECTOR).forEach((element) => {
      if (!(element instanceof HTMLElement)) return;

      if (isBusy) {
        element.setAttribute('aria-busy', 'true');
        return;
      }

      element.removeAttribute('aria-busy');
    });
  }

  /**
   * Synchronizes every custom wishlist button currently in the DOM.
   *
   * @param {{count: number, items: Array<object>}} snapshot
   * @returns {void}
   */
  function syncButtons(snapshot) {
    document.querySelectorAll(SMART_WISHLIST_BUTTON_SELECTOR).forEach((button) => {
      if (button instanceof HTMLElement) {
        syncButton(button, snapshot);
      }
    });
  }

  /**
   * Updates header wishlist icons when at least one item is saved.
   *
   * @param {{count: number, items: Array<object>}} snapshot
   * @returns {void}
   */
  function syncHeaderIcons(snapshot) {
    const hasItems = Number(snapshot.count) > 0;
    const label = hasItems
      ? `Wishlist, ${snapshot.count} ${snapshot.count === 1 ? 'item' : 'items'} saved`
      : 'Wishlist';

    document.querySelectorAll(SMART_WISHLIST_HEADER_SELECTOR).forEach((element) => {
      if (!(element instanceof HTMLElement)) return;

      element.dataset.voState = hasItems ? 'active' : 'inactive';
      element.classList.toggle('is-active', hasItems);
      element.setAttribute('aria-label', label);
    });
  }

  /**
   * Creates a stable product URL from a product handle.
   *
   * @param {string} handle
   * @returns {string}
   */
  function buildWishlistProductUrl(handle) {
    return `/products/${encodeURIComponent(String(handle || '').trim())}`;
  }

  /**
   * Resolves the localized alternatives collection URL for a wishlist sold-out CTA.
   *
   * @param {string} category
   * @param {{ sunglassesUrl?: string, eyeglassesUrl?: string }} alternativesUrls
   * @returns {string}
   */
  function getWishlistAlternativesUrl(category, alternativesUrls = {}) {
    const normalizedCategory = String(category || '').trim().toLowerCase();
    const sunglassesUrl = String(alternativesUrls.sunglassesUrl || '').trim();
    const eyeglassesUrl = String(alternativesUrls.eyeglassesUrl || '').trim();

    if (normalizedCategory === 'sun') {
      return sunglassesUrl || '/collections/sunglasses';
    }

    return eyeglassesUrl || '/collections/eyeglasses';
  }

  /**
   * Builds the Shopify section-rendering URL used to render the real PLP card snippet.
   *
   * @param {object} item
   * @returns {string}
   */
  function buildWishlistPlpCardSectionUrl(item) {
    const normalizedItem = normalizeWishlistItem(item) || {};
    const params = [];

    if (normalizedItem.variant_id) {
      params.push(`variant=${encodeURIComponent(String(normalizedItem.variant_id))}`);
    }

    params.push(`section_id=${encodeURIComponent(WISHLIST_PLP_CARD_SECTION_ID)}`);

    return `${buildWishlistProductUrl(normalizedItem.handle)}?${params.join('&')}`;
  }

  /**
   * Builds the product JSON endpoint used to recover PLP hover imagery.
   *
   * @param {string} handle
   * @returns {string}
   */
  function buildWishlistProductJsonUrl(handle) {
    return `/products/${encodeURIComponent(String(handle || '').trim())}.js`;
  }

  /**
   * Normalizes Shopify image URLs before comparing primary and hover images.
   *
   * @param {string} value
   * @returns {string}
   */
  function normalizeComparableImageUrl(value) {
    return String(value || '')
      .split('?')[0]
      .replace(/^https?:/, '')
      .replace(/_(pico|icon|thumb|small|compact|medium|large|grande|\d+x\d*|x\d+)(\.[a-z0-9]+)$/i, '$2');
  }

  /**
   * Returns a string URL from the image shapes exposed by Shopify endpoints.
   *
   * @param {unknown} image
   * @returns {string}
   */
  function getImageSource(image) {
    if (!image) return '';
    if (typeof image === 'string') return image;
    if (typeof image !== 'object') return '';

    return String(image.src || image.url || image.preview_image?.src || '');
  }

  /**
   * Normalizes a product JSON payload into only the fields wishlist cards need.
   *
   * @param {object} product
   * @returns {{featuredImage: string, images: Array<string>, variants: Array<object>, productType: string}|null}
   */
  function normalizeProductInfo(product) {
    if (!product || typeof product !== 'object') return null;

    const images = Array.isArray(product.images) ? product.images.map(getImageSource).filter(Boolean) : [];
    const featuredImage = getImageSource(product.featured_image) || images[0] || '';
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const productType = String(product.product_type || product.type || '')
      .trim()
      .toLowerCase();

    return {
      featuredImage,
      images,
      variants,
      productType,
    };
  }

  /**
   * Fetches public Shopify product data so wishlist cards can mirror PLP hover.
   *
   * @param {string} handle
   * @returns {Promise<{featuredImage: string, images: Array<string>, variants: Array<object>, productType: string}|null>}
   */
  function fetchWishlistProductInfo(handle) {
    const normalizedHandle = String(handle || '').trim();
    if (!normalizedHandle) return Promise.resolve(null);

    if (!productInfoCache.has(normalizedHandle)) {
      productInfoCache.set(
        normalizedHandle,
        fetchWithTimeout(buildWishlistProductJsonUrl(normalizedHandle))
          .then((response) => {
            if (!response.ok) return null;
            return response.json();
          })
          .then(normalizeProductInfo)
          .catch((error) => {
            console.warn(`Wishlist product data could not be loaded for "${normalizedHandle}".`, error);
            return null;
          })
      );
    }

    return productInfoCache.get(normalizedHandle);
  }

  /**
   * Picks the variant image from public product JSON when available.
   *
   * @param {{variants: Array<object>}|null} productInfo
   * @param {string} variantId
   * @returns {string}
   */
  function getVariantImageSource(productInfo, variantId) {
    if (!productInfo || !variantId) return '';

    const variant = productInfo.variants.find((candidate) => String(candidate.id || '') === String(variantId));
    return getImageSource(variant?.featured_image);
  }

  /**
   * Returns public product JSON variant availability when available.
   *
   * @param {{variants: Array<object>}|null} productInfo
   * @param {string} variantId
   * @returns {boolean|null}
   */
  function getVariantAvailability(productInfo, variantId) {
    if (!productInfo || !variantId) return null;

    const variant = productInfo.variants.find((candidate) => String(candidate.id || '') === String(variantId));
    if (!variant || typeof variant.available !== 'boolean') return null;

    return variant.available;
  }

  /**
   * Appends Shopify's JSON endpoint extension when the route does not include it.
   *
   * @param {string} url
   * @returns {string}
   */
  function withShopifyJsonExtension(url) {
    const normalizedUrl = String(url || '');
    return /\.js(?:$|\?)/.test(normalizedUrl) ? normalizedUrl : `${normalizedUrl}.js`;
  }

  /**
   * Builds the current cart JSON endpoint from Shopify routes.
   *
   * @returns {string}
   */
  function buildCartJsonUrl() {
    return withShopifyJsonExtension(window.routes?.cart_url || '/cart');
  }

  /**
   * Builds the add-to-cart JSON endpoint from Shopify routes.
   *
   * @returns {string}
   */
  function buildCartAddJsonUrl() {
    return withShopifyJsonExtension(window.routes?.cart_add_url || '/cart/add');
  }

  /**
   * Returns the variant ids currently present in the Shopify cart.
   *
   * @param {object} cart
   * @returns {Set<string>}
   */
  function normalizeCartVariantIds(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    return new Set(items.map((item) => String(item?.variant_id || '').trim()).filter(Boolean));
  }

  /**
   * Fetches the current cart so wishlist cards can show the already-in-cart state.
   *
   * @returns {Promise<Set<string>>}
   */
  async function fetchWishlistCartVariantIds() {
    try {
      const response = await fetchWithTimeout(buildCartJsonUrl());
      if (!response.ok) return new Set();

      return normalizeCartVariantIds(await response.json());
    } catch (error) {
      console.warn('Wishlist cart state could not be loaded.', error);
      return new Set();
    }
  }

  /**
   * Checks whether a wishlist item variant is already in the current cart.
   * Builds stock state from Shopify variant availability.
   *
   * @param {boolean|null} variantAvailable
   * @returns {object}
   */
  function buildWishlistStockState(variantAvailable) {
    if (variantAvailable === null) return {};

    return { variant_out_of_stock: variantAvailable ? 0 : 1 };
  }

  /**
   * Reads the discontinued (disco) flag from a rendered wishlist PLP card.
   *
   * @param {string} markup
   * @returns {boolean}
   */
  function parseWishlistVariantIsDisco(markup) {
    const match = String(markup || '').match(/\bdata-variant-is-disco=["'](true|false)["']/i);

    return match?.[1]?.toLowerCase() === 'true';
  }

  /**
   * Returns whether the saved wishlist variant had disco field set to true.
   *
   * @param {object} item
   * @param {Set<string>} cartVariantIds
   * @returns {boolean}
   */
  function isWishlistItemInCart(item, cartVariantIds) {
    const variantId = String(item?.variant_id || '').trim();
    return Boolean(variantId && cartVariantIds instanceof Set && cartVariantIds.has(variantId));
  }
  
  function isWishlistVariantDisco(item) {
    const value = item?.variant_is_disco;

    return value === true || value === 1 || String(value).toLowerCase() === 'true';
  }

  /**
   * Chooses the next distinct product image after the primary image.
   *
   * @param {string} primaryImage
   * @param {Array<string>} images
   * @returns {string}
   */
  function pickHoverImage(primaryImage, images) {
    const normalizedImages = Array.isArray(images) ? images.filter(Boolean) : [];
    if (normalizedImages.length < 2) return '';

    const primaryKey = normalizeComparableImageUrl(primaryImage);
    const primaryIndex = normalizedImages.findIndex((image) => normalizeComparableImageUrl(image) === primaryKey);
    const orderedImages =
      primaryIndex >= 0
        ? normalizedImages.slice(primaryIndex + 1).concat(normalizedImages.slice(0, primaryIndex))
        : normalizedImages;

    return orderedImages.find((image) => normalizeComparableImageUrl(image) !== primaryKey) || '';
  }

  /**
   * Adds product imagery needed by the PLP hover layer to wishlist items.
   *
   * @param {Array<object>} items
   * @returns {Promise<Array<object>>}
   */
  async function enrichWishlistItems(items) {
    const normalizedItems = Array.isArray(items) ? items.map(normalizeWishlistItem).filter(Boolean) : [];

    return Promise.all(
      normalizedItems.map(async (item) => {
        const itemImages = Array.isArray(item.images) ? item.images.map(getImageSource).filter(Boolean) : [];
        const existingPrimaryImage = item.image || item.featured_image || item.featuredImage || itemImages[0] || '';
        const existingHoverImage =
          item.hover_image || item.hoverImage || pickHoverImage(existingPrimaryImage, itemImages);
        const needsImages = item.handle && !existingHoverImage;
        const needsStockState = Boolean(item.handle && item.variant_id);
        const needsCategory = Boolean(item.handle && !item.category);
        const needsProductInfo = needsImages || needsStockState || needsCategory;

        if (!needsProductInfo) {
          return {
            ...item,
            image: existingPrimaryImage,
            hover_image: existingHoverImage,
          };
        }

        const productInfo = await fetchWishlistProductInfo(item.handle);
        const variantImage = getVariantImageSource(productInfo, item.variant_id);
        const primaryImage = existingPrimaryImage || variantImage || productInfo?.featuredImage || productInfo?.images?.[0] || '';
        const productImages = productInfo?.images || [];
        const hoverImage = pickHoverImage(primaryImage, productImages);
        const variantAvailable = getVariantAvailability(productInfo, item.variant_id);
        const recoveredStockState = buildWishlistStockState(variantAvailable);

        return {
          ...item,
          ...recoveredStockState,
          category: productInfo?.productType || item.category || '',
          image: primaryImage,
          hover_image: existingHoverImage || hoverImage,
        };
      })
    );
  }

  /**
   * Formats a raw Smart Wishlist price string for storefront display.
   *
   * @param {string|number} value
   * @returns {string}
   */
  function formatWishlistPrice(value) {
    const numericValue = Number.parseFloat(value);
    if (!Number.isFinite(numericValue)) return '';

    const currencyCode = window.Shopify?.currency?.active;
    const locale = document.documentElement.lang || undefined;

    if (currencyCode) {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: numericValue % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(numericValue);
    }

    return numericValue.toFixed(2);
  }

  /**
   * Builds the wishlist-only add-to-cart controls that sit below the PLP card content.
   *
   * @param {object} item
   * @param {Record<string, string>} texts
   * @param {Set<string>} cartVariantIds
   * @returns {string}
   */
  function buildWishlistAddToCartMarkup(item, texts, cartVariantIds = new Set()) {
    const normalizedItem = normalizeWishlistItem(item) || {};
    const variantId = escapeHtml(normalizedItem.variant_id || '');
    const isUnavailable = Number(normalizedItem.variant_out_of_stock) === 1 || !variantId;
    const isInCart = isWishlistItemInCart(normalizedItem, cartVariantIds);
    const handle = String(normalizedItem.handle || '').trim();
    const productUrl = handle ? buildWishlistProductUrl(handle) : '';

    if (isInCart) {
      return `
        <div class="tw-flex tw-flex-col tw-gap-base tw-w-full tw-mt-auto tw-pt-base" data-vo-wishlist-card-actions>
          <a
            class="${WISHLIST_ACTION_LINK_CLASS}"
            href="${escapeHtml(texts.continueShoppingUrl || '/collections/all')}"
            data-vo-wishlist-continue-shopping
            data-variant-id="${variantId}"
          >
            ${escapeHtml(texts.continueShoppingLabel)}
          </a>
        </div>
      `;
    }

    const category = escapeHtml(normalizedItem.category || '');
    if (isUnavailable && productUrl && !isWishlistVariantDisco(normalizedItem)) {
      return `
        <div class="tw-flex tw-flex-col tw-gap-base tw-w-full tw-mt-auto tw-pt-xs lg:tw-pt-sm" data-vo-wishlist-card-actions>
          <button
            type="button"
            class="${WISHLIST_ACTION_BUTTON_CLASS}"
            data-vo-wishlist-notify-me
            data-variant-id="${variantId}"
            data-product-url="${escapeHtml(productUrl)}"
          >
            ${escapeHtml(texts.notifyMeLabel)}
          </button>
        </div>
      `;
    }

    return `
      <div class="tw-flex tw-flex-col tw-gap-base tw-w-full tw-mt-auto tw-pt-xs lg:tw-pt-sm" data-vo-wishlist-card-actions>
        <button
          type="button"
          class="${WISHLIST_ADD_TO_CART_BUTTON_CLASS}"
          ${isUnavailable ? 'data-vo-wishlist-sold-out' : 'data-vo-wishlist-add-to-cart'}
          data-variant-id="${variantId}"
          data-category="${category}"
        >
          ${escapeHtml(isUnavailable ? texts.soldOutLabel : texts.addToCartLabel)}
        </button>
      </div>
    `;
  }

  /**
   * Extracts the PLP card from Shopify section-rendering wrappers.
   *
   * @param {string} markup
   * @returns {string}
   */
  function extractWishlistPlpCardMarkup(markup) {
    const match = String(markup || '').match(/<vo-plp-card\b[\s\S]*?<\/vo-plp-card>/);
    return match ? match[0].trim() : '';
  }

  /**
   * Adds wishlist page metadata and actions around markup rendered by the PLP card snippet.
   *
   * @param {string} markup
   * @param {object} item
   * @param {Record<string, string>} texts
   * @param {Set<string>} cartVariantIds
   * @returns {string}
   */
  function decorateWishlistPlpCardMarkup(markup, item, texts, cartVariantIds = new Set()) {
    const normalizedItem = normalizeWishlistItem(item) || {};
    const productId = escapeHtml(normalizedItem.product_id || '');
    const variantId = escapeHtml(normalizedItem.variant_id || '');
    const trimmedMarkup = extractWishlistPlpCardMarkup(markup);

    if (!trimmedMarkup) return '';

    const cardMarkup = trimmedMarkup.replace(/<vo-plp-card\b([^>]*)>/, (match, attributes) => {
      if (/\sdata-vo-wishlist-card(\s|=|>)/.test(match)) return match;

      return `<vo-plp-card${attributes} data-vo-wishlist-card data-product-id="${productId}" data-variant-id="${variantId}">`;
    });
    const statusBadgeMatch = trimmedMarkup.match(/<[a-z][a-z0-9]*\b[^>]*\bvo-plp-card__status\b[^>]*>/i);
    const hasStatusBadge = Boolean(statusBadgeMatch) && !/\shidden(\s|>|\/|=)/.test(statusBadgeMatch[0]);
    const itemForActions = hasStatusBadge ? { ...normalizedItem, variant_out_of_stock: 1 } : normalizedItem;
    const actionsMarkup = buildWishlistAddToCartMarkup(
      { ...normalizedItem, variant_is_disco: parseWishlistVariantIsDisco(trimmedMarkup) },
      texts
    );
    const contentEndPattern = /(\s*<\/div>\s*<\/article>\s*<\/vo-plp-card>\s*)$/;

    if (contentEndPattern.test(cardMarkup)) {
      return cardMarkup.replace(contentEndPattern, `${actionsMarkup}$1`);
    }

    return cardMarkup.replace(/<\/vo-plp-card>\s*$/, `${actionsMarkup}</vo-plp-card>`);
  }

  /**
   * Fetches the real PLP product card snippet through Shopify's section rendering API.
   *
   * @param {object} item
   * @param {Record<string, string>} texts
   * @param {Set<string>} cartVariantIds
   * @returns {Promise<string>}
   */
  async function fetchWishlistPlpCardMarkup(item, texts, cartVariantIds = new Set()) {
    const normalizedItem = normalizeWishlistItem(item) || {};
    if (!normalizedItem.handle) return '';

    const requestUrl = buildWishlistPlpCardSectionUrl(normalizedItem);
    const cacheKey = `${normalizedItem.handle}:${normalizedItem.variant_id || ''}`;

    if (!wishlistPlpCardMarkupCache.has(cacheKey)) {
      wishlistPlpCardMarkupCache.set(
        cacheKey,
        (async () => {
          const response = await fetchWithTimeout(requestUrl, 'text/html');
          if (!response.ok || typeof response.text !== 'function') return '';

          return response.text();
        })().catch((error) => {
          console.warn('Wishlist PLP card section could not be rendered.', error);
          return '';
        })
      );
    }

    const sectionMarkup = await wishlistPlpCardMarkupCache.get(cacheKey);
    return decorateWishlistPlpCardMarkup(sectionMarkup, normalizedItem, texts, cartVariantIds);
  }

  /**
   * Builds the fallback markup for a rendered wishlist card on the custom wishlist page.
   *
   * @param {object} item
   * @param {Record<string, string>} texts
   * @param {Set<string>} cartVariantIds
   * @returns {string}
   */
  function buildFallbackWishlistCardMarkup(item, texts, cartVariantIds = new Set()) {
    const normalizedItem = normalizeWishlistItem(item) || {};
    const productTitle = escapeHtml(normalizedItem.title || '');
    const hasVariantTitle = normalizedItem.variant_title && normalizedItem.variant_title !== 'Default Title';
    const metaMarkup = hasVariantTitle
      ? `<div class="vo-plp-card__meta"><span class="tw-typo-vo-sm tw-text-vo-neutral">${escapeHtml(normalizedItem.variant_title)}</span></div>`
      : '<div class="vo-plp-card__meta" aria-hidden="true"><span class="tw-typo-vo-sm tw-text-vo-neutral">&nbsp;</span></div>';
    const productUrl = buildWishlistProductUrl(normalizedItem.handle);
    const productId = escapeHtml(normalizedItem.product_id || '');
    const variantId = escapeHtml(normalizedItem.variant_id || '');
    const priceLabel = formatWishlistPrice(normalizedItem.variant_price);
    const isUnavailable = Number(normalizedItem.variant_out_of_stock) === 1 || !variantId;
    const cardStateClass = isUnavailable ? ' vo-plp-card--unavailable' : '';
    const mediaGroupStateClass = isUnavailable ? ' is-unavailable' : '';
    const galleryAvailable = isUnavailable ? 'false' : 'true';
    const removeLabel = productTitle ? `Remove ${productTitle} from wishlist` : 'Remove from wishlist';
    const statusMarkup = isUnavailable
      ? `<p class="vo-plp-card__status tw-typo-vo-sm tw-text-vo-dark">${escapeHtml(texts.soldOutLabel)}</p>`
      : '';
    const priceMarkup = `<div class="vo-plp-card__price vo-plp-card__price--stacked${
      priceLabel ? '' : ' vo-plp-card__price--empty'
    }"><span class="vo-plp-card__price-current tw-typo-vo-sm tw-text-vo-dark"${
      priceLabel ? '' : ' aria-hidden="true"'
    }>${priceLabel ? escapeHtml(priceLabel) : '&nbsp;'}</span></div>`;
    const imageMarkup = normalizedItem.image
      ? `<img class="vo-plp-card__media-image vo-plp-card__media-primary" src="${escapeHtml(normalizedItem.image)}" alt="${productTitle}" loading="lazy">`
      : '<div class="vo-plp-card__media-fallback vo-plp-card__media-primary" aria-hidden="true"></div>';
    const hoverImage = normalizedItem.hover_image || normalizedItem.hoverImage || '';
    const hoverImageMarkup =
      hoverImage && normalizeComparableImageUrl(hoverImage) !== normalizeComparableImageUrl(normalizedItem.image)
        ? `<div class="vo-plp-card__media-layer vo-plp-card__media-layer--hover" data-gallery-hover hidden>
            <img class="vo-plp-card__media-image vo-plp-card__media-image--hover" src="${escapeHtml(hoverImage)}" alt="${productTitle}" loading="lazy">
          </div>`
        : '';

    return `
      <vo-plp-card class="vo-plp-card${cardStateClass}" data-vo-wishlist-card data-product-id="${productId}" data-variant-id="${variantId}">
        <article class="vo-plp-card__article">
          <div class="vo-plp-card__media-shell">
            <a class="vo-plp-card__media-link" href="${productUrl}" aria-label="${productTitle}">
              <div class="vo-plp-card__media-frame">
                <div class="vo-plp-card__media-asset">
                  <div class="vo-plp-card__media-group is-active${mediaGroupStateClass}" data-gallery-group="0" data-gallery-available="${galleryAvailable}">
                    <div class="vo-plp-card__media-layer vo-plp-card__media-layer--primary" data-gallery-primary>
                      ${imageMarkup}
                    </div>
                    ${hoverImageMarkup}
                  </div>
                </div>
              </div>
            </a>

            <div class="vo-plp-card__topline">
              <div class="vo-plp-card__badge"></div>
              <button
                type="button"
                class="vo-plp-card__wishlist is-active"
                data-vo-wishlist-remove
                data-product-id="${productId}"
                data-variant-id="${variantId}"
                data-product-title="${productTitle}"
                aria-label="${removeLabel}"
                aria-pressed="true"
              >
                ${HEART_ICON}
              </button>
            </div>
          </div>

          <div class="vo-plp-card__content">
            <div class="vo-plp-card__header tw-w-full tw-flex tw-flex-col tw-items-start md:tw-flex-row md:tw-justify-between md:tw-items-center">
              <h3 class="vo-plp-card__title">
                <a href="${productUrl}" class="tw-typo-vo-sm tw-text-vo-dark">${productTitle}</a>
              </h3>
              ${priceMarkup}
            </div>
            ${metaMarkup}
            ${statusMarkup}
            ${buildWishlistAddToCartMarkup(normalizedItem, texts, cartVariantIds)}
          </div>
        </article>
      </vo-plp-card>
    `;
  }

  /**
   * Builds the markup for a rendered wishlist card on the custom wishlist page.
   *
   * @param {object} item
   * @param {Record<string, string>} texts
   * @param {Set<string>} cartVariantIds
   * @returns {Promise<string>}
   */
  async function buildWishlistCardMarkup(item, texts, cartVariantIds = new Set()) {
    return (
      (await fetchWishlistPlpCardMarkup(item, texts, cartVariantIds)) ||
      buildFallbackWishlistCardMarkup(item, texts, cartVariantIds)
    );
  }

  /**
   * Builds the optional media used by the anonymous account prompt.
   *
   * @param {Record<string, string>} texts
   * @returns {string}
   */
  function buildWishlistLoginImageMarkup(texts) {
    if (!texts.loginImageUrl) return '';

    const mobileSource = texts.loginMobileImageUrl
      ? `<source srcset="${escapeHtml(texts.loginMobileImageUrl)}" media="(max-width: 989px)">`
      : '';

    return `
      <picture data-vo-wishlist-login-media>
        ${mobileSource}
        <img
          class="tw-block tw-w-full tw-h-full tw-object-cover"
          src="${escapeHtml(texts.loginImageUrl)}"
          alt="${escapeHtml(texts.loginImageAlt || texts.loginTitle)}"
          loading="lazy"
          data-vo-wishlist-login-image
        >
      </picture>
    `;
  }

  /**
   * Builds the account prompt shown to anonymous wishlist users.
   *
   * @param {Record<string, string>} texts
   * @returns {string}
   */
  function buildWishlistLoginCardMarkup(texts) {
    const hasImage = Boolean(texts.loginImageUrl);

    return `
      <aside class="tw-relative tw-flex tw-min-h-[120px] lg:tw-min-h-[220px] tw-flex-col tw-justify-end tw-gap-xl tw-col-span-full lg:tw-col-span-1 tw-p-xl lg:tw-p-4xl tw-overflow-hidden tw-bg-vo-neutral-light tw-text-vo-dark" data-vo-wishlist-login-card ${
        hasImage ? 'data-vo-wishlist-login-has-image' : ''
      }>
        ${buildWishlistLoginImageMarkup(texts)}
        <div class="tw-relative tw-z-10 tw-flex tw-flex-col tw-justify-end tw-gap-xl tw-w-full tw-h-full" data-vo-wishlist-login-content>
          <div class="tw-flex tw-flex-col tw-gap-8">
            <h2 class="tw-m-0 tw-typo-vo-h2 tw-text-vo-dark" data-vo-wishlist-login-title>${escapeHtml(texts.loginTitle)}</h2>
            <p class="tw-m-0 tw-typo-vo-base tw-text-vo-dark" data-vo-wishlist-login-text>${escapeHtml(texts.loginText)}</p>
          </div>
          <div class="tw-flex tw-flex-col tw-gap-base tw-w-full">
            <a class="tw-vo-btn-filled tw-w-full tw-text-center tw-no-underline" href="${escapeHtml(
              texts.loginUrl
            )}" data-vo-wishlist-login-action="login">
              ${escapeHtml(texts.loginLabel)}
            </a>
            <a class="tw-vo-btn-outline tw-w-full tw-text-center tw-no-underline" href="${escapeHtml(
              texts.registerUrl
            )}" data-vo-wishlist-login-action="register">
              ${escapeHtml(texts.registerLabel)}
            </a>
          </div>
        </div>
      </aside>
    `;
  }

  /**
   * Renders and handles interactions for the custom Vogue wishlist page.
   */
  class VoWishlistPage {
    /**
     * @param {HTMLElement} element
     */
    constructor(element) {
      this.element = element;
      this.loadingElement = element.querySelector('[data-vo-wishlist-loading]');
      this.emptyElement = element.querySelector('[data-vo-wishlist-empty]');
      this.gridElement = element.querySelector('[data-vo-wishlist-grid]');
      this.loginTemplate = element.querySelector('[data-vo-wishlist-login-template]');
      this.statusElement = element.querySelector('[data-vo-wishlist-status]');
      this.onClick = this.onClick.bind(this);
      this.cartVariantIds = new Set();
      this.renderToken = 0;
      this.element.addEventListener('click', this.onClick);
    }

    /**
     * Returns the configurable copy used by the wishlist page renderer.
     *
     * @returns {Record<string, string>}
     */
    getTexts() {
      return {
        addToCartLabel: this.element.dataset.addToCartLabel || 'Add to cart',
        continueShoppingLabel: this.element.dataset.continueShoppingLabel || 'Continue shopping',
        continueShoppingUrl: this.element.dataset.continueShoppingUrl || '/collections/all',
        loadingLabel: this.element.dataset.loadingLabel || 'Loading your wishlist...',
        loginImageAlt: this.element.dataset.loginImageAlt || this.element.dataset.loginTitle || "Don't lose access to your favorites.",
        loginImageUrl: this.element.dataset.loginImageUrl || '',
        loginLabel: this.element.dataset.loginLabel || 'Sign in',
        loginMobileImageUrl: this.element.dataset.loginMobileImageUrl || this.element.dataset.loginImageUrl || '',
        loginText: this.element.dataset.loginText || 'Sign in or register now.',
        loginTitle: this.element.dataset.loginTitle || "Don't lose access to your favorites.",
        loginUrl: this.element.dataset.loginUrl || '/account/login',
        registerLabel: this.element.dataset.registerLabel || 'Create an account',
        registerUrl: this.element.dataset.registerUrl || '/account/register',
        notifyMeLabel: this.element.dataset.notifyMeLabel || 'Email me when available',
        soldOutLabel: this.element.dataset.soldOutLabel || 'Continue Shopping',
        viewProductLabel: this.element.dataset.viewProductLabel || 'View product',
      };
    }

    /**
     * Announces a short live-region message on the page.
     *
     * @param {string} message
     * @returns {void}
     */
    announce(message) {
      if (!(this.statusElement instanceof HTMLElement)) return;
      this.statusElement.textContent = message;
    }

    /**
     * Toggles the loading state for the page.
     *
     * @param {boolean} isLoading
     * @returns {void}
     */
    setLoading(isLoading) {
      if (!(this.loadingElement instanceof HTMLElement)) return;
      this.loadingElement.hidden = !isLoading;
    }

    /**
     * Renders the empty state when the wishlist contains no items.
     *
     * @returns {void}
     */
    renderEmpty() {
      this.renderToken += 1;

      if (this.gridElement instanceof HTMLElement) {
        this.gridElement.innerHTML = '';
        this.gridElement.hidden = true;
      }

      if (this.emptyElement instanceof HTMLElement) {
        this.emptyElement.hidden = false;
      }
    }

    /**
     * Renders the wishlist product grid when data is available.
     *
     * @param {{count: number, items: Array<object>}} snapshot
     * @returns {void}
     */
    async renderItems(snapshot) {
      if (!(this.gridElement instanceof HTMLElement)) return;

      const renderToken = (this.renderToken += 1);
      const texts = this.getTexts();
      const [wishlistItems, cartVariantIds] = await Promise.all([
        enrichWishlistItems(snapshot.items),
        fetchWishlistCartVariantIds(),
      ]);
      if (renderToken !== this.renderToken) return;

      this.cartVariantIds = cartVariantIds;

      const cards = await Promise.all(wishlistItems.map((item) => buildWishlistCardMarkup(item, texts, cartVariantIds)));
      if (renderToken !== this.renderToken) return;

      if (!getCustomerWishlistId()) {
        const insertIndex = Math.min(cards.length, 3);
        const loginCardMarkup =
          this.loginTemplate instanceof HTMLTemplateElement
            ? this.loginTemplate.innerHTML.trim()
            : buildWishlistLoginCardMarkup(texts);
        cards.splice(insertIndex, 0, loginCardMarkup);
      }

      this.gridElement.innerHTML = cards.join('');
      initWishlistButtons(this.gridElement);
      syncButtons(sharedState.snapshot);
      this.gridElement.hidden = false;

      if (this.emptyElement instanceof HTMLElement) {
        this.emptyElement.hidden = true;
      }
    }

    /**
     * Renders the entire page state from the latest wishlist snapshot.
     *
     * @param {{count: number, items: Array<object>}} snapshot
     * @returns {void}
     */
    render(snapshot) {
      this.setLoading(false);

      if (!snapshot.count) {
        this.renderEmpty();
        return;
      }

      this.renderItems(snapshot).catch((error) => {
        console.warn('Wishlist items could not be rendered.', error);
        this.renderEmpty();
      });
    }

    /**
     * Adds a wishlist variant to the Shopify cart using the Ajax cart API.
     *
     * @param {HTMLButtonElement} button
     * @param {string} variantId
     * @returns {Promise<void>}
     */
    async addVariantToCart(button, variantId) {
      const normalizedVariantId = String(variantId || '').trim();
      if (!/^\d+$/.test(normalizedVariantId)) return;

      button.disabled = true;
      let didAddToCart = false;

      const cart =
        document.querySelector('vo-cart-notification') ||
        document.querySelector('[data-vo-cart-notification]') ||
        document.querySelector('cart-notification') ||
        document.querySelector('cart-drawer');

      try {
        const body = new FormData();
        body.append('id', normalizedVariantId);
        body.append('quantity', '1');
        body.append('properties[_added_at]', `${new Date().toISOString()}-wishlist-${Math.random().toString(36).slice(2, 8)}`);

        if (cart && typeof cart.getSectionsToRender === 'function') {
          const sectionIds = cart.getSectionsToRender().map((section) => section.id);
          body.append('sections', sectionIds.join(','));
          body.append('sections_url', window.location.pathname);
          if (typeof cart.setActiveElement === 'function') {
            cart.setActiveElement(button);
          }
        }

        const response = await fetch(buildCartAddJsonUrl(), {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body,
        });

        const payload = await response.json();
        if (!response.ok || payload.status) {
          throw new Error(payload?.description || 'Unable to add this item to cart.');
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'vo-smartwishlist',
            productVariantId: normalizedVariantId,
            cartData: payload,
          });
        }

        if (cart && typeof cart.renderContents === 'function' && payload.sections) {
          cart.renderContents(payload);
        }

        didAddToCart = true;
        this.cartVariantIds.add(normalizedVariantId);
        this.updateVariantCartState(normalizedVariantId);
        this.announce('Item added to cart.');
      } catch (error) {
        this.announce(error instanceof Error ? error.message : 'Unable to add this item to cart.');
      } finally {
        if (!didAddToCart) {
          button.disabled = false;
        }
      }
    }

    /**
     * Replaces add-to-cart buttons for a variant with the already-in-cart state.
     *
     * @param {string} variantId
     * @returns {void}
     */
    updateVariantCartState(variantId) {
      const normalizedVariantId = String(variantId || '').trim();
      if (!normalizedVariantId) return;

      this.element
        .querySelectorAll(`[data-vo-wishlist-add-to-cart][data-variant-id="${normalizedVariantId}"]`)
        .forEach((button) => {
          if (button instanceof HTMLButtonElement) {
            this.replaceAddToCartButtonWithContinueShopping(button);
          }
        });
    }

    /**
     * Swaps a wishlist add-to-cart button to the continue-shopping CTA.
     *
     * @param {HTMLButtonElement} button
     * @returns {void}
     */
    replaceAddToCartButtonWithContinueShopping(button) {
      const texts = this.getTexts();
      const link = document.createElement('a');

      link.className = WISHLIST_ACTION_LINK_CLASS;
      link.href = texts.continueShoppingUrl || '/collections/all';
      link.textContent = texts.continueShoppingLabel || 'Continue shopping';
      link.setAttribute('data-vo-wishlist-continue-shopping', '');
      link.setAttribute('data-variant-id', button.dataset.variantId || '');

      button.replaceWith(link);
    }

    /**
     * Handles clicks on the wishlist page remove and add-to-cart actions.
     *
     * @param {MouseEvent} event
     * @returns {void}
     */
    onClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const removeButton = target.closest('[data-vo-wishlist-remove]');
      if (removeButton instanceof HTMLButtonElement && this.element.contains(removeButton)) {
        event.preventDefault();

        const productId = Number(removeButton.dataset.productId);
        const variantId = Number(removeButton.dataset.variantId);
        if (!Number.isFinite(productId) || !Number.isFinite(variantId)) return;

        if (typeof window.RemoveFromSmartWishlist === 'function') {
          window.RemoveFromSmartWishlist(productId, variantId);
          this.announce(`${removeButton.dataset.productTitle || 'Item'} removed from wishlist.`);
          scheduleWishlistRefresh(150);
        }

        return;
      }

      const addToCartButton = target.closest('[data-vo-wishlist-add-to-cart]');
      if (addToCartButton instanceof HTMLButtonElement && this.element.contains(addToCartButton)) {
        event.preventDefault();
        this.addVariantToCart(addToCartButton, addToCartButton.dataset.variantId || '');
        return;
      }

      const notifyButton = target.closest('[data-vo-wishlist-notify-me]');
      if (notifyButton instanceof HTMLButtonElement && this.element.contains(notifyButton)) {
        event.preventDefault();
        this.openNotifyModal(notifyButton);
      }

      const soldOutButton = target.closest('[data-vo-wishlist-sold-out]');
      if (soldOutButton instanceof HTMLButtonElement && this.element.contains(soldOutButton)) {
        event.preventDefault();
        const alternativesUrl = getWishlistAlternativesUrl(soldOutButton.dataset.category, {
          sunglassesUrl: this.element.dataset.sunglassesUrl,
          eyeglassesUrl: this.element.dataset.eyeglassesUrl,
        });
        if (alternativesUrl) {
          window.location.assign(alternativesUrl);
        }
        return;
      }
    }

    /**
     * Fetches the PDP notify-me dialog for the given product variant, injects it into the
     * current page, and opens it as a modal — mirroring the PDP "Email me when available" CTA.
     *
     * @param {HTMLButtonElement} button
     * @returns {Promise<void>}
     */
    async openNotifyModal(button) {
      const productUrl = button.dataset.productUrl;
      const variantId = button.dataset.variantId;
      if (!productUrl) return;

      button.disabled = true;
      try {
        const url = new URL(productUrl, window.location.origin);
        if (variantId) url.searchParams.set('variant', variantId);
        url.searchParams.set('section_id', 'main-product');

        const response = await fetch(url.toString(), { credentials: 'same-origin' });
        if (!response.ok) throw new Error('Unable to load notify form.');

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const dialog = doc.querySelector('dialog[id^="VoNotifyOverlay-"]');
        if (!(dialog instanceof HTMLElement)) throw new Error('Notify form not available for this product.');

        document.querySelectorAll('dialog[data-vo-wishlist-notify-modal]').forEach((existing) => existing.remove());

        dialog.setAttribute('data-vo-wishlist-notify-modal', '');
        document.body.appendChild(dialog);

        const cleanup = () => {
          dialog.removeEventListener('close', cleanup);
          dialog.remove();
        };
        dialog.addEventListener('close', cleanup);

        if (typeof window.openOverlayDialog === 'function') {
          window.openOverlayDialog(dialog, button);
        } else if (typeof openOverlayDialog === 'function') {
          openOverlayDialog(dialog, button);
        } else if (dialog instanceof HTMLDialogElement) {
          dialog.showModal();
        }

        const activate = typeof window.activateNotifyAppDialog === 'function'
          ? window.activateNotifyAppDialog
          : typeof activateNotifyAppDialog === 'function'
            ? activateNotifyAppDialog
            : null;
        if (activate) {
          setTimeout(() => activate(dialog), 0);
        }
      } catch (error) {
        this.announce(error instanceof Error ? error.message : 'Unable to open notify form.');
      } finally {
        button.disabled = false;
      }
    }
  }

  /**
   * Creates page controllers for any wishlist page roots inside the given scope.
   *
   * @param {ParentNode} root
   * @returns {void}
   */
  function initWishlistPages(root = document) {
    if (!root || typeof root.querySelectorAll !== 'function') return;

    const elements =
      root instanceof Element && root.matches(SMART_WISHLIST_PAGE_SELECTOR)
        ? [root]
        : Array.from(root.querySelectorAll(SMART_WISHLIST_PAGE_SELECTOR));

    elements.forEach((element) => {
      if (!(element instanceof HTMLElement) || sharedState.pageControllers.has(element)) return;
      sharedState.pageControllers.set(element, new VoWishlistPage(element));
    });
  }

  /**
   * Renders every initialized wishlist page with the latest snapshot.
   *
   * @param {{count: number, items: Array<object>}} snapshot
   * @returns {void}
   */
  function renderWishlistPages(snapshot) {
    sharedState.pageControllers.forEach((controller, element) => {
      if (!element.isConnected) {
        sharedState.pageControllers.delete(element);
        return;
      }

      controller.render(snapshot);
    });
  }

  /**
   * Attaches the custom wishlist click handler to a theme button once.
   *
   * @param {HTMLElement} button
   * @returns {void}
   */
  function initializeWishlistButton(button) {
    if (sharedState.initializedButtons.has(button)) return;

    button.addEventListener('click', handleWishlistButtonClick);
    sharedState.initializedButtons.add(button);

    if (!button.dataset.voState) {
      setButtonState(button, false);
    }

    if (!sharedState.hasLoadedSnapshot) {
      button.setAttribute('aria-busy', 'true');
      return;
    }

    syncButton(button, sharedState.snapshot);
  }

  /**
   * Creates button handlers for any wishlist buttons inside the given scope.
   *
   * @param {ParentNode} root
   * @returns {void}
   */
  function initWishlistButtons(root = document) {
    if (!root || typeof root.querySelectorAll !== 'function') return;

    const elements =
      root instanceof Element && root.matches(SMART_WISHLIST_BUTTON_SELECTOR)
        ? [root]
        : Array.from(root.querySelectorAll(SMART_WISHLIST_BUTTON_SELECTOR));

    elements.forEach((button) => {
      if (button instanceof HTMLElement) {
        initializeWishlistButton(button);
      }
    });
  }

  /**
   * Schedules a forced snapshot refresh after the Smart Wishlist app mutates data.
   *
   * @param {number} delay
   * @param {{ showBusy?: boolean }} options
   * @returns {void}
   */
  function scheduleWishlistRefresh(delay = 250, { showBusy = false } = {}) {
    window.clearTimeout(sharedState.refreshTimeout);
    sharedState.refreshTimeout = window.setTimeout(() => {
      refreshWishlistUI({ force: true, showBusy });
    }, delay);
  }

  /**
   * Dispatches a local event whenever wishlist data may have changed.
   *
   * @param {Record<string, unknown>} detail
   * @returns {void}
   */
  function dispatchWishlistMutation(detail = {}) {
    window.dispatchEvent(
      new CustomEvent('vo:smartwishlist:changed', {
        detail,
      })
    );
    scheduleWishlistRefresh(100, { showBusy: false });
  }

  /**
   * Wraps a Smart Wishlist callback so existing behavior is preserved while the
   * custom theme UI can re-sync itself after mutations.
   *
   * @param {string} callbackName
   * @returns {void}
   */
  function wrapSmartWishlistCallback(callbackName) {
    const existingCallback = window[callbackName];
    if (existingCallback?.__voSmartWishlistWrapped) return;

    const wrappedCallback = function wrappedSmartWishlistCallback(...args) {
      if (typeof existingCallback === 'function') {
        existingCallback.apply(this, args);
      }

      dispatchWishlistMutation({ callbackName, args });
    };

    wrappedCallback.__voSmartWishlistWrapped = true;
    window[callbackName] = wrappedCallback;
  }

  /**
   * Hooks into the Smart Wishlist callbacks so external mutations re-render the
   * custom Vogue UI automatically.
   *
   * @returns {void}
   */
  function subscribeToSmartWishlistCallbacks() {
    SMART_WISHLIST_CALLBACKS.forEach((callbackName) => {
      wrapSmartWishlistCallback(callbackName);
    });
  }

  /**
   * Re-synchronizes buttons, counts, and pages against the latest snapshot.
   *
   * @param {{ force?: boolean, showBusy?: boolean }} options
   * @returns {Promise<void>}
   */
  async function refreshWishlistUI({ force = false, showBusy = true } = {}) {
    if (force && showBusy) {
      sharedState.snapshot = EMPTY_SNAPSHOT;
      sharedState.hasLoadedSnapshot = false;
    }

    if (showBusy) {
      setWishlistControlsBusy(true);
    }

    try {
      const snapshot = await fetchWishlistSnapshot({ force });
      sharedState.hasLoadedSnapshot = true;
      syncWishlistCount(snapshot.count);
      syncHeaderIcons(snapshot);
      syncButtons(snapshot);
      renderWishlistPages(snapshot);
    } finally {
      if (showBusy) {
        setWishlistControlsBusy(false);
      } else {
        document.querySelectorAll(SMART_WISHLIST_BUTTON_SELECTOR).forEach((button) => {
          if (button instanceof HTMLElement) {
            button.removeAttribute('aria-busy');
          }
        });
      }
    }
  }

  /**
   * Toggles a custom wishlist button by calling the Smart Wishlist JS API.
   *
   * @param {HTMLElement} button
   * @returns {void}
   */
  function toggleWishlistButton(button) {
    const { productId, variantId } = getButtonIds(button);
    const savedVariantId = String(button.dataset.voSmartwishlistSavedVariantId || '');
    const isActive = button.dataset.voState === 'active';
    const targetVariantId = isActive && savedVariantId ? savedVariantId : variantId;
    const normalizedProductId = Number(productId);
    const normalizedVariantId = Number(targetVariantId);

    if (!Number.isFinite(normalizedProductId) || !Number.isFinite(normalizedVariantId)) return;

    const mutationFunction = isActive ? window.RemoveFromSmartWishlist : window.AddToSmartWishlist;

    if (typeof mutationFunction !== 'function') {
      console.warn('Smart Wishlist JavaScript API is unavailable.');
      return;
    }

    button.setAttribute('aria-busy', 'true');
    setButtonState(button, !isActive);

    try {
      const mutationItem = {
        product_id: productId,
        variant_id: targetVariantId,
        handle: button.dataset.productHandle || '',
        title: button.dataset.productTitle || '',
      };

      mutationFunction(normalizedProductId, normalizedVariantId);
      recordPendingWishlistMutation(isActive ? 'remove' : 'add', mutationItem);
      if (typeof window.localStorage?.getItem === 'function') {
        const cachedItems = readCachedWishlist();
        const nextItems = isActive
          ? cachedItems.filter((item) => String(item.variant_id) !== targetVariantId)
          : [
              ...cachedItems.filter((item) => String(item.variant_id) !== variantId),
              mutationItem,
            ];

        setWishlistSessionMetaForCurrentOwner();
        writeCachedWishlist(nextItems);
        sharedState.snapshot = {
          count: nextItems.length,
          items: nextItems,
        };
        syncWishlistCount(sharedState.snapshot.count);
        syncHeaderIcons(sharedState.snapshot);
        syncButtons(sharedState.snapshot);
        button.setAttribute('aria-busy', 'true');
      }

      dispatchWishlistMutation({
        action: isActive ? 'remove' : 'add',
        productId: normalizedProductId,
        variantId: normalizedVariantId,
      });
    } catch (error) {
      console.warn('Smart Wishlist mutation failed.', error);
      setButtonState(button, isActive);
      button.removeAttribute('aria-busy');
    }
  }

  /**
   * Handles clicks on theme wishlist buttons.
   *
   * @param {MouseEvent} event
   * @returns {void}
   */
  function handleWishlistButtonClick(event) {
    const button = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();
    toggleWishlistButton(button);
  }

  /**
   * Keeps the PDP wishlist button aligned with the currently selected variant.
   *
   * @param {{ data?: { sectionId?: string, variant?: { id?: number|string } } }} event
   * @returns {void}
   */
  function handleVariantChange(event) {
    const sectionId = String(event?.data?.sectionId || '');
    const variantId = String(event?.data?.variant?.id || '');
    if (!sectionId || !variantId) return;

    document
      .querySelectorAll(`[data-vo-smartwishlist-variant-sync][data-section-id="${sectionId}"]`)
      .forEach((button) => {
        if (!(button instanceof HTMLElement)) return;
        button.dataset.variantId = variantId;
        if (sharedState.hasLoadedSnapshot) {
          syncButton(button, sharedState.snapshot);
        }
      });
  }

  /**
   * Observes the DOM for newly inserted wishlist controls and page roots.
   *
   * @returns {void}
   */
  function observeWishlistDom() {
    if (sharedState.buttonObserver || !(document.body instanceof HTMLBodyElement)) return;

    sharedState.buttonObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          initWishlistButtons(node);
          initWishlistPages(node);
        });
      });
    });

    sharedState.buttonObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Boots the custom Vogue Smart Wishlist integration once the DOM is ready.
   *
   * @returns {void}
   */
  function initializeWishlist() {
    if (sharedState.isInitialized) return;
    sharedState.isInitialized = true;

    const hydratedFromCache = hydrateWishlistSnapshotFromCache();

    initWishlistButtons(document);
    initWishlistPages(document);
    if (hydratedFromCache) {
      syncButtons(sharedState.snapshot);
      renderWishlistPages(sharedState.snapshot);
    }
    subscribeToSmartWishlistCallbacks();
    observeWishlistDom();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, handleVariantChange);
    }

    syncWishlistSessionState().finally(() => {
      refreshWishlistUI({ force: true, showBusy: !hydratedFromCache });
    });
  }

  /**
   * Re-syncs wishlist state when the page is restored from the browser cache.
   *
   * @param {PageTransitionEvent} event
   * @returns {void}
   */
  function handleWishlistPageShow(event) {
    if (!event.persisted) return;

    syncWishlistSessionState().finally(() => {
      refreshWishlistUI({ force: true });
    });
  }

  class VoSmartWishlist extends HTMLElement {
    connectedCallback() {
      initializeWishlist();

      if (!sharedState.pageShowHandler) {
        sharedState.pageShowHandler = handleWishlistPageShow;
        window.addEventListener('pageshow', sharedState.pageShowHandler);
      }
    }
  }

  if (!customElements.get('vo-smartwishlist')) {
    customElements.define('vo-smartwishlist', VoSmartWishlist);
  }
}
